import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { classNames } from 'primereact/utils';
import { TabPanel, TabView } from 'primereact/tabview';
import { DataScroller } from 'primereact/datascroller';
import { Checkbox } from 'primereact/checkbox';
import Cookies from 'universal-cookie';
import { debounce } from 'lodash';

import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { generateFullName } from 'utils/generateFullNameUtils';
import { getRelativeTime, replaceLawftDate } from 'utils/utils';
import constants from 'constants/index';
import ImageComponent from 'shared/ImageComponent';
import { SentimentDot } from 'components/SentimentDot/SentimentDot';
import { useContacts } from 'hooks/useContacts';
import { usePracticeAreaList } from 'hooks/usePracticeAreaList';
import { MultiSelect } from 'primereact/multiselect';
import { handleFilterUpdateInUserContext } from 'common/pagination';
import { useNavbarContext } from 'context/NavbarContext';
import { Button } from 'primereact/button';
import { listMessageHistory } from 'services/messages/textMessageService';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';

const cookie = new Cookies();

const contactOptionTemplate = (item) => {
  let name = generateFullName(item);
  if (!item?.phone) {
    name += ' (Please add a phone number)';
  }
  return (
    <div
      className={classNames({ 'disable-option': !Boolean(item?.phone) })}
      title={name}
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}
    >
      {name}
    </div>
  );
};

const tenantId = cookie.get('tenantId');

export default function ThreadList({
  isFromCase,
  caseDetails,
  isFromLead,
  leadDetails,
  selectedContact,
  setSelectedContact,
  messageThreads,
  setMessageThreads,
  selectedThread,
  handleSelectThread,
  activeIndex,
  setActiveIndex,
  loadThreads,
  isLoading,
  selectedPracticeAreas,
  setSelectedPracticeAreas,
  setPageCount,
  setIsLastPage,
  selectedThreadsForMarkRead,
  setSelectedThreadsForMarkRead,
  onMarkAsRead,
  isSelectAllActive,
  setIsSelectAllActive,
}) {
  const userContext = useUserDetailsContext();
  const history = useHistory();
  const location = useLocation();
  const [caseRelatedContactList, setCaseRelatedContactList] = useState([]);
  const [practiceAreaOptions, setPracticeAreaOptions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState(selectedContact);
  const [showNoResults, setShowNoResults] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const { contactList, searchAsync, loading: contactsLoading } = useContacts(isFromLead, '');
  const { practiceAreaList } = usePracticeAreaList();
  const navbarContext = useNavbarContext();
  const loadNotificationCount = navbarContext?.notificationData?.loadNotificationCount;
  const setTextMessageFilterActive = navbarContext?.setTextMessageFilterActive;
  const hasInitialized = useRef(false);
  const scrollContainerRef = useRef(null);
  const hasRestoredFromUrl = useRef(false);
  const isInitialMount = useRef(true);
  const previousFirstThreadRef = useRef(null);
  const prevPracticeAreaListLengthRef = useRef(0);

  useEffect(() => {
    if (practiceAreaOptions?.length && !hasInitialized.current) {
      hasInitialized.current = true;
      const savedPracticeAreas = userContext?.userDetails?.user_preferences?.filters?.text_messages?.practice_area || [];

      setSelectedPracticeAreas(savedPracticeAreas);

      // Update filter state based on saved preferences
      // Note: loadNotificationCount will be called by useTextMessage hook when selectedPracticeAreas changes
      if (setTextMessageFilterActive) {
        setTextMessageFilterActive(savedPracticeAreas?.length > 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceAreaOptions]);

  // Track search results and show "No results found" message
  useEffect(() => {
    if (!contactsLoading && lastSearchQuery && lastSearchQuery.length > 0) {
      if (!contactList || contactList.length === 0) {
        setShowNoResults(true);
      } else {
        setShowNoResults(false);
      }
    }
  }, [contactList, contactsLoading, lastSearchQuery]);

  // Auto-scroll to top when thread order changes (e.g., after sending a message)
  useEffect(() => {
    if (messageThreads?.length > 0) {
      const currentFirstThread = messageThreads[0];
      const currentFirstThreadId = currentFirstThread?.sk || currentFirstThread?.id;

      // Check if first thread changed (not initial mount)
      if (previousFirstThreadRef.current && previousFirstThreadRef.current !== currentFirstThreadId) {
        // Scroll to top smoothly
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      }

      // Update the ref to current first thread
      previousFirstThreadRef.current = currentFirstThreadId;
    }
  }, [messageThreads]);

  // Update selectAll state based on selected threads
  useEffect(() => {
    const allThreadPks = messageThreads.map(
      (thread) => `${tenantId}#${thread?.to === userContext?.firmPhoneNumber ? thread?.from_ : thread?.to}`
    );

    if (allThreadPks.length > 0) {
      const allSelected = allThreadPks.every((pk) => selectedThreadsForMarkRead.includes(pk));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedThreadsForMarkRead, messageThreads, userContext?.firmPhoneNumber]);

  // Clear selections when switching tabs (but keep query param)
  useEffect(() => {
    setSelectedThreadsForMarkRead([]);
    setSelectAll(false);
    if (setIsSelectAllActive) {
      setIsSelectAllActive(false);
    }
  }, [activeIndex, setSelectedThreadsForMarkRead, setIsSelectAllActive]);

  // Cleanup: Reset filter state when component unmounts
  useEffect(() => {
    return () => {
      if (setTextMessageFilterActive) {
        setTextMessageFilterActive(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('page_reloaded', 'true');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const wasReloaded = sessionStorage.getItem('page_reloaded');

    if (wasReloaded === 'true') {
      sessionStorage.removeItem('page_reloaded');

      const searchParams = new URLSearchParams(location.search);

      if (searchParams.has('contactId')) {
        searchParams.delete('contactId');

        history.replace({
          pathname: location.pathname,
          search: searchParams.toString() ? `?${searchParams.toString()}` : '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Restore thread from URL query parameter on mount or when returning from navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const contactIdFromUrl = searchParams.get('contactId');

    // If we have messageThreads loaded
    if (messageThreads.length > 0) {
      if (contactIdFromUrl) {
        // Check if the thread is already selected to avoid unnecessary updates
        const isAlreadySelected = selectedThread?.contact_id === contactIdFromUrl;

        if (!isAlreadySelected && !hasRestoredFromUrl.current) {
          // Try to find and restore the thread
          const threadToRestore = messageThreads.find((thread) => thread.contact_id === contactIdFromUrl);

          if (threadToRestore) {
            handleSelectThread(threadToRestore);
            hasRestoredFromUrl.current = true;
          } else {
            // Thread not found in list - clear the query param (page refresh scenario)
            searchParams.delete('contactId');
            const newSearch = searchParams.toString();
            history.replace({
              pathname: location.pathname,
              search: newSearch ? `?${newSearch}` : '',
            });
          }
        }
        // Mark initial mount as complete after attempting restoration
        if (isInitialMount.current) {
          isInitialMount.current = false;
        }
      } else {
        // No contactId in URL, mark initial mount as complete
        if (isInitialMount.current) {
          isInitialMount.current = false;
        }
      }
    }
  }, [messageThreads, location.search, handleSelectThread, selectedThread, history, location.pathname]);

  // Clear query param when no contact and no thread is selected
  useEffect(() => {
    // Don't clear query param on initial mount - wait for restoration to complete
    if (isInitialMount.current) {
      return;
    }

    if ((!selectedContact || selectedContact === '' || selectedContact === null) && !selectedThread) {
      // Remove contactId query param only when both contact and thread are cleared
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.has('contactId')) {
        searchParams.delete('contactId');
        const newSearch = searchParams.toString();
        history.replace({
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        });
      }
    }
  }, [selectedContact, selectedThread, location.pathname, history]);

  // Manual scroll listener for pagination
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = debounce(() => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      // Trigger load when scrolled 80% down
      if (scrollPercentage >= 0.8) {
        loadThreads();
      }
    }, 300);

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [loadThreads]);

  useEffect(() => {
    if (isFromCase && caseDetails) {
      let contactList = [];
      if (caseDetails?.client_details?.length > 0) {
        contactList.push(caseDetails?.client_details[0]);
      }
      if (caseDetails?.relations?.length > 0) {
        contactList = [...contactList, ...caseDetails?.relations];
      }
      const mappedContactList = contactList.map((v) => ({ ...v, fullName: generateFullName(v) }));
      // Sort contacts alphabetically
      const sortedContactList = mappedContactList.sort((a, b) => {
        const nameA = a?.first_name || a?.title || '';
        const nameB = b?.first_name || b?.title || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });
      setCaseRelatedContactList(sortedContactList);
    }

    if (isFromLead && leadDetails) {
      let allLeadContacts = [];
      const { main_client, relations } = leadDetails || {};

      if (main_client?.length) {
        allLeadContacts = [...allLeadContacts, main_client[0]];
      }
      if (relations?.length) {
        allLeadContacts = [...allLeadContacts, ...relations];
      }
      const mappedLeadContacts = allLeadContacts.map((v) => ({ ...v, fullName: generateFullName(v) }));
      // Sort contacts alphabetically using the logic: first_name || title
      const sortedLeadContacts = mappedLeadContacts.sort((a, b) => {
        const nameA = a?.first_name || a?.title || '';
        const nameB = b?.first_name || b?.title || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });
      setCaseRelatedContactList(sortedLeadContacts);
    }
  }, [caseDetails, leadDetails]);

  useEffect(() => {
    if (practiceAreaList && practiceAreaList?.length) {
      let practice_options = practiceAreaList?.map((val) => {
        return {
          label: val.practice_area_name,
          value: val.practice_area_id,
        };
      });
      setPracticeAreaOptions(practice_options);

      // Track practice area list length for reference
      const currentLength = practiceAreaList.length;
      prevPracticeAreaListLengthRef.current = currentLength;
    } else {
      setPracticeAreaOptions([]);
      prevPracticeAreaListLengthRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceAreaList]);

  const handleSearchAsync = async (e) => {
    setShowNoResults(false);
    setLastSearchQuery(e.query || '');
    await searchAsync(e);
  };

  const handleSelectContact = (e) => {
    // Always update autocompleteValue to show what user is typing/selecting
    setAutocompleteValue(e.value);

    // Hide "no results" message when selecting or typing
    if (e.value && typeof e.value === 'object') {
      setShowNoResults(false);
      setLastSearchQuery('');
    }

    // Only set selectedContact when a contact is actually selected (object with contact_id)
    if (typeof e.value === 'object' && e.value?.contact_id) {
      if (e.value?.phone === '' || e.value?.phone === undefined) {
        // Don't set if phone is missing
        // setSelectedContact('');
      } else {
        setSelectedContact(e.value);
        // Don't call history API here - it will be called when thread is selected from text-message-threads-v2 response
      }
    } else if (e.value === '' || e.value === null || e.value === undefined) {
      // Clear selection when field is cleared
      setSelectedContact(e.value);
      setAutocompleteValue(null);
      setShowNoResults(false);
      setLastSearchQuery('');
      // Clear query param when contact is deselected
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.has('contactId')) {
        searchParams.delete('contactId');
        const newSearch = searchParams.toString();
        history.replace({
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        });
      }
    }
    // If e.value is a string (typing), only autocompleteValue is updated, not selectedContact
  };

  const handleThreadClick = (message) => {
    handleSelectThread(message);
    message.is_read = true;

    // Set contact ID in query parameter while preserving other params
    if (message?.contact_id) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('contactId', message.contact_id);
      history.replace({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    }

    // If we're in the Unread tab (activeIndex === 1), remove the message from the list
    if (activeIndex === 1) {
      setMessageThreads((prevThreads) => prevThreads.filter((thread) => thread.sk !== message.sk));

      // Update notification count when a thread is clicked in the Unread tab
      // This is necessary because we're removing the thread from the list
      if (loadNotificationCount) {
        loadNotificationCount(selectedPracticeAreas?.length > 0 ? selectedPracticeAreas : []);
      }
    } else {
      // Otherwise, just update the is_read status
      setMessageThreads((prevThreads) => prevThreads.map((thread) => (thread.sk === message.sk ? { ...thread, is_read: true } : thread)));
    }
  };

  const handleCheckboxChange = (message, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    const messagePk = `${tenantId}#${message?.to === userContext?.firmPhoneNumber ? message?.from_ : message?.to}`;

    setSelectedThreadsForMarkRead((prevSelected) => {
      const newSelected = prevSelected.includes(messagePk) ? prevSelected.filter((pk) => pk !== messagePk) : [...prevSelected, messagePk];
      return newSelected;
    });

    // Individual checkbox click means select all is no longer active
    if (setIsSelectAllActive) {
      setIsSelectAllActive(false);
    }
  };

  const handleSelectAllChange = (e) => {
    e.stopPropagation();
    const isChecked = e.checked;

    if (isChecked) {
      // Select all threads in current view
      const allThreadPks = messageThreads.map(
        (thread) => `${tenantId}#${thread?.to === userContext?.firmPhoneNumber ? thread?.from_ : thread?.to}`
      );
      setSelectedThreadsForMarkRead(allThreadPks);
      // Set flag to indicate select all is active
      if (setIsSelectAllActive) {
        setIsSelectAllActive(true);
      }
    } else {
      // Deselect all
      setSelectedThreadsForMarkRead([]);
      if (setIsSelectAllActive) {
        setIsSelectAllActive(false);
      }
    }
    setSelectAll(isChecked);
  };

  const handleLastMessage = (msg = false) => {
    try {
      if (msg && msg.last_message) {
        let unicode_1 = '#128206; ';
        let unicode_2 = '&#128206; ';
        let unicode_3 = '128206';
        let data1 = msg.last_message.replace(unicode_1, '');
        let data2 = replaceLawftDate(data1.replace(unicode_2, ''), userContext?.userDetails?.timezone);
        if (msg.last_message.includes(unicode_1) || msg.last_message.includes(unicode_2) || msg.last_message.includes(unicode_3)) {
          return (
            <span>
              <i className="fas fa-paperclip"></i>
              {` ${data2}`}
            </span>
          );
        } else {
          return data2;
        }
      }
    } catch (error) {}
  };

  const threadItemTemplate = (message, index) => {
    const sentimentParams = {
      type: message?.aggregate_sentiment,
      positive: message?.positive_sentiment_count,
      neutral: message?.neutral_sentiment_count,
      negative: message?.negative_sentiment_count,
      mixed: message?.mixed_sentiment_count,
    };
    const messagePk = `${tenantId}#${message?.to === userContext?.firmPhoneNumber ? message?.from_ : message?.to}`;
    const isChecked = selectedThreadsForMarkRead.includes(messagePk);
    const isUnread = message?.is_read === false;
    const showCheckbox = true; // Always show checkbox for all messages

    return (
      <div
        className={`w-100 border-top p-3 ps-2 pointer d-flex align-items-center${
          selectedThread?.sk === message.sk ? ' selected-thread-bg' : ''
        }`}
        key={index}
        onClick={() => handleThreadClick(message)}
        style={selectedThread?.sk === message.sk ? { background: 'var(--selected-msg)' } : {}}
      >
        {/* Checkbox */}
        {showCheckbox && (
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: '14px', minWidth: '14px' }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                handleCheckboxChange(message, e);
              }}
            />
          </div>
        )}

        {/* Avatar */}
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0 ms-2 overflow-hidden"
          style={{
            width: '40px',
            height: '40px',
            minWidth: '40px',
            minHeight: '40px',
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <ImageComponent
            filePath={`${tenantId}/${message?.contact_id}/profile_image`}
            fileName={
              message?.client_name?.first_name?.charAt(0) || message?.client_name?.title?.charAt(0) || message?.title?.charAt(0) || 'C'
            }
            style={{
              objectFit: 'cover',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              backgroundColor: '#2196F3',
              color: '#ffffff',
            }}
            fileSize="medium.jpg"
          />
        </div>

        {/* Name, Phone, and Message */}
        <div className="d-flex flex-column flex-grow-1 min-w-0 px-2 gap-1">
          <div className="d-flex align-items-center flex-wrap min-w-0">
            <h5 className="title-text mb-0 text-truncate min-w-0 flex-grow-1">
              {message?.client_name?.first_name || message?.client_name?.title || message?.title
                ? `${generateFullName(message?.client_name) || message?.title || 'User'} ( ${
                    userContext?.firmPhoneNumber === message.from_ ? message?.to : message?.from_
                  } )`
                : 'User'}
            </h5>
          </div>

          <span
            className={`pointer text-primary-dark ${message?.is_read ? '' : 'text-bold'} text-truncate`}
            style={{
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {message.last_author !== 'client' ? (
              <>
                <span>{message.last_author}:</span>&nbsp;
                {handleLastMessage(message)}
              </>
            ) : (
              handleLastMessage(message)
            )}
          </span>
        </div>

        {/* Date and "New" badge */}
        <div className="d-flex flex-column align-items-end text-end flex-shrink-0" style={{ minWidth: '80px' }}>
          <div className="black-600">
            {getRelativeTime(
              convertDateFromApiToUserTimezone(message.last_updated, userContext?.userDetails?.timezone, constants.date_time_format)
            )}
          </div>
          <div className="d-flex flex-column align-items-end gap-1">
            {message?.is_read === false && <span className="tag-green F-size12 mt-1 text-nowrap">New</span>}
            {message?.contact_status === 'ARCHIVED' && (
              <span className="badge-text mt-1 tag-red text-nowrap F-size12">
                <i className="fas fa-exclamation-circle me-1 white"></i>Inactive Contact
              </span>
            )}
            {message?.contact_status === 'ACTIVE' &&
              (() => {
                // Check if phone number has changed
                const threadPhone = message?.to || message?.phone;
                const latestPhone = message?.latest_phone_number;
                if (!threadPhone || !latestPhone) return false;

                const normalize = (phone) => {
                  let normalized = phone.replace(/[^\d]/g, ''); // Remove all non-digits
                  if (normalized.startsWith('1') && normalized.length === 11) {
                    normalized = normalized.substring(1); // Remove leading 1 for US numbers
                  }
                  return normalized;
                };

                return normalize(threadPhone) !== normalize(latestPhone);
              })() && (
                <span className="badge-text mt-1 tag-red text-nowrap F-size12">
                  <i className="fas fa-exclamation-circle me-1 white"></i>Inactive Thread
                </span>
              )}
          </div>
        </div>
      </div>
    );
  };

  const handleSelectPracticeArea = (e) => {
    const selectedAreas = e?.value ? e?.value : [];
    setSelectedPracticeAreas(selectedAreas);
    setPageCount(1);
    setIsLastPage(false);
    handleFilterUpdateInUserContext(userContext, 'filters', 'text_messages', null, 'practice_area', selectedAreas, false);

    // Note: loadNotificationCount will be called by useTextMessage hook when selectedPracticeAreas changes
    // No need to call it here to avoid duplicate API calls

    // Update filter active state
    if (setTextMessageFilterActive) {
      setTextMessageFilterActive(selectedAreas.length > 0);
    }
  };

  return (
    <div className="card-bg mt-3 text-message-filter tab-view h-100 overflow-hidden d-flex flex-column">
      <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header="All"></TabPanel>
        <TabPanel header="Unread"></TabPanel>
      </TabView>
      <div className="p-3 contact-select">
        <span className="p-input-icon-left w-100">
          <div className="text-message-filter-row-responsive">
            <div className="text-message-filter-col">
              {Boolean(isFromCase || isFromLead) ? (
                <Dropdown
                  name="dropdown"
                  value={selectedContact}
                  optionLabel="fullName"
                  options={caseRelatedContactList}
                  onChange={handleSelectContact}
                  placeholder="Select a contact"
                  className="input-shadow w-100"
                  optionValue={(v) => v}
                  filter
                  showClear
                  itemTemplate={contactOptionTemplate}
                  panelStyle={{ width: '10%' }}
                />
              ) : (
                <div style={{ position: 'relative', width: '100%' }}>
                  <AutoComplete
                    value={autocompleteValue}
                    selectedItemTemplate={(v) => {
                      if (typeof v === 'object' && v?.contact_id) {
                        return generateFullName(v);
                      }
                      return v || '';
                    }}
                    name="autoComplete"
                    suggestions={contactList || []}
                    completeMethod={handleSearchAsync}
                    onChange={handleSelectContact}
                    placeholder="Search or Start New Chat"
                    className="input-shadow w-100 text-message-contact-autocomplete"
                    inputClassName="w-100"
                    itemTemplate={contactOptionTemplate}
                    resetFilterOnHide
                    panelStyle={{ minWidth: '300px', maxWidth: '500px' }}
                    emptyMessage="No results found"
                    showEmptyMessage={true}
                    forceSelection={false}
                    minLength={1}
                  />
                  {autocompleteValue && (
                    <i
                      className="pi pi-times"
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        color: '#6c757d',
                        fontSize: '0.875rem',
                        zIndex: 1,
                      }}
                      onClick={() => {
                        setAutocompleteValue(null);
                        setSelectedContact('');
                        setShowNoResults(false);
                        setLastSearchQuery('');
                        // Clear query param when search is cleared
                        const searchParams = new URLSearchParams(location.search);
                        if (searchParams.has('contactId')) {
                          searchParams.delete('contactId');
                          const newSearch = searchParams.toString();
                          history.replace({
                            pathname: location.pathname,
                            search: newSearch ? `?${newSearch}` : '',
                          });
                        }
                      }}
                    />
                  )}
                  {showNoResults && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        padding: '8px 12px',
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        color: '#6c757d',
                        fontSize: '0.875rem',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                      }}
                    >
                      No results found
                    </div>
                  )}
                </div>
              )}
            </div>
            {!isFromCase && !isFromLead && (
              <div className="text-message-filter-col">
                <MultiSelect
                  name="practice_area"
                  value={selectedPracticeAreas}
                  options={practiceAreaOptions}
                  onChange={(e) => handleSelectPracticeArea(e)}
                  placeholder="Select Practice Area"
                  display="chip"
                  filter
                  showClear
                  className="input-shadow w-100 text-message-practice-area-multiselect"
                  panelStyle={{ minWidth: '300px', maxWidth: '500px' }}
                />
              </div>
            )}
          </div>
        </span>
      </div>
      {messageThreads.length > 0 && (
        <div className="w-100 p-3 pt-0">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-center px-2 justify-content-center" style={{ width: '14px', minWidth: '14px' }}>
                <Checkbox checked={selectAll} onChange={handleSelectAllChange} inputId="selectAllCheckbox" />
              </div>
              <label htmlFor="selectAllCheckbox" className="ms-2 mb-0 caption pointer">
                {activeIndex === 1 ? `Select All Unread (${messageThreads.length})` : `Select All (${messageThreads.length})`}
              </label>
            </div>
            {selectedThreadsForMarkRead.length > 0 && (
              <Button className="p-button p-button-primary outline ms-2" onClick={() => onMarkAsRead && onMarkAsRead()}>
                Mark as Read
              </Button>
            )}
          </div>
        </div>
      )}
      <div
        ref={scrollContainerRef}
        className="msg-holder pt-0 pb-0 position-relative flex-grow-1"
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          scrollbarWidth: 'thin', // For Firefox
          msOverflowStyle: 'auto', // For IE/Edge
        }}
      >
        {isLoading && (
          <div className="chat-loader-container pe-3">
            <div className="overlay">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2em' }}></i>
            </div>
          </div>
        )}
        <DataScroller
          value={messageThreads}
          emptyMessage="No threads found"
          itemTemplate={threadItemTemplate}
          rows={15}
          inline
          lazy
          buffer={0.4}
          onLazyLoad={(e) => {
            loadThreads();
          }}
        />
      </div>
    </div>
  );
}
